import React, { useMemo, useState } from 'react';
import { consoleTokens } from '@components/ui/console';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { ReportColumn, ReportFilterSpec } from '../types';

const MAX_FILTERS = 5;

// Operators by column type — mirrors the backend whitelist exactly.
const OPS_BY_TYPE: Record<string, { value: string; label: string }[]> = {
  string: [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' },
    { value: 'any_of', label: 'is any of' },
  ],
  int: [
    { value: 'eq', label: '=' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
  ],
  bool: [{ value: 'is', label: 'is' }],
  date: [
    { value: 'before', label: 'before' },
    { value: 'after', label: 'after' },
  ],
};
OPS_BY_TYPE.decimal = OPS_BY_TYPE.int;
OPS_BY_TYPE.cents = OPS_BY_TYPE.int;
OPS_BY_TYPE.datetime = OPS_BY_TYPE.date;

const EMPTY_OPS = [
  { value: 'is_empty', label: 'is empty' },
  { value: 'not_empty', label: 'is not empty' },
];

const opsForType = (type: string): { value: string; label: string }[] => [
  ...(OPS_BY_TYPE[type] ?? OPS_BY_TYPE.string),
  ...EMPTY_OPS,
];

const needsValue = (op: string): boolean => op !== 'is_empty' && op !== 'not_empty';

// Readable op labels for the saved-view preview ("Charges ≥ $150").
const OP_LABELS: Record<string, string> = {
  is: 'is',
  is_not: 'is not',
  any_of: 'is any of',
  eq: '=',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  before: 'before',
  after: 'after',
  is_empty: 'is empty',
  not_empty: 'is not empty',
};

/** One-line human description of a filter, e.g. "Payer class is Medicaid". */
export const describeFilter = (
  filter: ReportFilterSpec,
  columns: ReportColumn[],
): string => {
  const column = columns.find(item => item.key === filter.field);
  const label = column?.label ?? filter.field;
  const op = OP_LABELS[filter.op] ?? filter.op;
  if (!needsValue(filter.op)) return `${label} ${op}`;
  const values = filter.values ?? [];
  const shown =
    column?.type === 'cents'
      ? values.map(value => {
          const dollars = Number(value) / 100;
          return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
        })
      : values.map(String);
  return `${label} ${op} ${shown.join(', ')}`;
};

// Font only — heights stay MUI's size="small" default so text fields sit
// flush with the selects beside them.
const fieldSx = {
  '& .MuiInputBase-root': { fontSize: '0.75rem' },
};

export interface FilterPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Filterable detail columns, in definition order. */
  columns: ReportColumn[];
  filters: ReportFilterSpec[];
  onChange: (next: ReportFilterSpec[]) => void;
  /** Distinct values of a column across the loaded result rows. */
  suggestionsFor: (field: string) => string[];
}

/**
 * The `filter:` token editor — one row per active filter (column · op ·
 * value), values suggested from the loaded result's own rows. AND semantics;
 * the backend's whitelist is the source of truth for what's legal.
 */
export const FilterPopover: React.FC<FilterPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  columns,
  filters,
  onChange,
  suggestionsFor,
}) => {
  const [draftField, setDraftField] = useState('');
  const columnsByKey = useMemo(
    () => Object.fromEntries(columns.map(column => [column.key, column])),
    [columns],
  );

  const updateFilter = (index: number, next: Partial<ReportFilterSpec>) => {
    onChange(
      filters.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...next } : item,
      ),
    );
  };

  const removeFilter = (index: number) => {
    onChange(filters.filter((_, itemIndex) => itemIndex !== index));
  };

  const addFilter = (field: string) => {
    const column = columnsByKey[field];
    if (!column) return;
    const firstOp = opsForType(column.type)[0].value;
    const initialValues: ReportFilterSpec['values'] =
      column.type === 'bool' ? [true] : [];
    onChange([...filters, { field, op: firstOp, values: initialValues }]);
    setDraftField('');
  };

  const renderValueEditor = (item: ReportFilterSpec, index: number) => {
    const column = columnsByKey[item.field];
    if (!column || !needsValue(item.op)) return <Box sx={{ flex: 1 }} />;

    if (column.type === 'bool') {
      return (
        <Select
          size="small"
          value={String(item.values?.[0] ?? true)}
          onChange={event =>
            updateFilter(index, { values: [event.target.value === 'true'] })
          }
          sx={{ flex: 1, fontSize: '0.75rem' }}
        >
          <MenuItem value="true" sx={{ fontSize: '0.75rem' }}>
            yes
          </MenuItem>
          <MenuItem value="false" sx={{ fontSize: '0.75rem' }}>
            no
          </MenuItem>
        </Select>
      );
    }

    if (['int', 'decimal', 'cents'].includes(column.type)) {
      // Money columns: the user types dollars, the API filters cents.
      const isCents = column.type === 'cents';
      const stored = item.values?.[0];
      const shown =
        stored === undefined || stored === ''
          ? ''
          : isCents
            ? String(Number(stored) / 100)
            : String(stored);
      return (
        <TextField
          size="small"
          type="number"
          placeholder={isCents ? 'amount ($)' : 'value'}
          value={shown}
          onChange={event => {
            const raw = event.target.value;
            updateFilter(index, {
              values:
                raw === ''
                  ? []
                  : [isCents ? Math.round(Number(raw) * 100) : Number(raw)],
            });
          }}
          sx={{ flex: 1, ...fieldSx }}
        />
      );
    }

    if (['date', 'datetime'].includes(column.type)) {
      return (
        <TextField
          size="small"
          type="date"
          value={String(item.values?.[0] ?? '')}
          onChange={event => updateFilter(index, { values: [event.target.value] })}
          sx={{ flex: 1, ...fieldSx }}
        />
      );
    }

    const multiple = item.op === 'any_of';
    const options = suggestionsFor(item.field);
    return (
      <Autocomplete
        size="small"
        freeSolo
        multiple={multiple}
        options={options}
        // The anchor input is narrow; let the suggestion list size to its
        // content instead of inheriting that width, and keep options on
        // one dense line each.
        slotProps={{
          popper: {
            placement: 'bottom-start',
            sx: { minWidth: 220, maxWidth: 400, width: 'auto !important' },
          },
          chip: { size: 'small', sx: { fontSize: '0.6875rem', height: 20 } },
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option}
            sx={{ fontSize: '0.75rem !important', whiteSpace: 'nowrap' }}
          >
            {option}
          </Box>
        )}
        value={
          multiple
            ? (item.values ?? []).map(String)
            : String(item.values?.[0] ?? '')
        }
        onChange={(_, next) =>
          updateFilter(index, {
            values: multiple
              ? ((next as string[]) ?? [])
              : next == null || next === ''
                ? []
                : [next as string],
          })
        }
        onInputChange={(_, next, reason) => {
          if (!multiple && reason === 'input') {
            updateFilter(index, { values: next === '' ? [] : [next] });
          }
        }}
        renderInput={params => (
          <TextField {...params} size="small" placeholder="value" sx={fieldSx} />
        )}
        sx={{ flex: 1, minWidth: 160 }}
      />
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { width: 500 } } }}
    >
      <Box sx={{ p: 1.5 }}>
        {filters.length === 0 && (
          <Typography sx={{ color: consoleTokens.dim, fontSize: '0.75rem', pb: 1 }}>
            Only rows matching every filter appear — in the table, the stats,
            and exports.
          </Typography>
        )}

        <Stack spacing={1}>
          {filters.map((item, index) => {
            const column = columnsByKey[item.field];
            return (
              <Stack key={`${item.field}-${index}`} direction="row" spacing={1}>
                <Select
                  size="small"
                  value={item.field}
                  onChange={event => {
                    const nextColumn = columnsByKey[event.target.value];
                    updateFilter(index, {
                      field: event.target.value,
                      op: opsForType(nextColumn?.type ?? 'string')[0].value,
                      values: nextColumn?.type === 'bool' ? [true] : [],
                    });
                  }}
                  sx={{ flexShrink: 0, fontSize: '0.75rem', width: 150 }}
                >
                  {columns.map(option => (
                    <MenuItem
                      key={option.key}
                      value={option.key}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  size="small"
                  value={item.op}
                  onChange={event => {
                    const op = event.target.value;
                    updateFilter(index, {
                      op,
                      values: needsValue(op) ? item.values : [],
                    });
                  }}
                  sx={{ flexShrink: 0, fontSize: '0.75rem', width: 104 }}
                >
                  {opsForType(column?.type ?? 'string').map(option => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {renderValueEditor(item, index)}
                <IconButton
                  size="small"
                  aria-label="Remove filter"
                  onClick={() => removeFilter(index)}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>

        {filters.length < MAX_FILTERS && (
          <Select
            size="small"
            displayEmpty
            value={draftField}
            onChange={event => addFilter(event.target.value)}
            sx={{
              color: consoleTokens.dim,
              fontFamily: consoleTokens.mono,
              fontSize: '0.6875rem',
              mt: filters.length > 0 ? 1.5 : 0,
              width: 180,
            }}
          >
            <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>
              + add filter…
            </MenuItem>
            {columns.map(option => (
              <MenuItem
                key={option.key}
                value={option.key}
                sx={{ fontSize: '0.75rem' }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </Box>
    </Popover>
  );
};
